import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { PlatformUserDetail } from '@starter/shared';
import { api, ApiError } from '../../api/client.js';
import { ErrorBanner } from '../../components/ErrorBanner.js';
import { LoadingState } from '../../components/LoadingState.js';
import { PageHeader } from '../../components/PageHeader.js';

export function PlatformUserDetailPage() {
  const { userId } = useParams<{ userId: string }>();
  const [user, setUser] = useState<PlatformUserDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmDeactivate, setConfirmDeactivate] = useState(false);

  const load = useCallback(() => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    api
      .platformGetUser(userId)
      .then(setUser)
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Failed to load user'))
      .finally(() => setLoading(false));
  }, [userId]);

  useEffect(load, [load]);

  async function toggleStatus() {
    if (!user || busy) return;
    if (user.status === 'active') {
      if (!confirmDeactivate) {
        setConfirmDeactivate(true);
        return;
      }
    }
    setBusy(true);
    setStatusError(null);
    try {
      const next = user.status === 'active' ? 'inactive' : 'active';
      const updated = await api.platformSetUserStatus(user.id, next);
      setUser(updated);
      setConfirmDeactivate(false);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Status update failed';
      setStatusError(message);
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <LoadingState message="Loading user…" />;
  if (error && !user) return <ErrorBanner message={error} onRetry={load} />;
  if (!user) return null;

  return (
    <div className="page">
      <PageHeader
        title={user.fullName}
        subtitle={user.email}
        actions={<Link to="/platform/users" className="btn btn--ghost">Back to users</Link>}
      />
      {error && <ErrorBanner message={error} onRetry={load} />}

      <section className="card">
        <h2 className="card__title">Account</h2>
        <p>
          Status: <span className={`status-pill status-pill--${user.status}`}>{user.status}</span>
        </p>
        {user.platformAdmin && (
          <p>Platform admin: <span className="status-pill">{user.platformAdmin.status}</span></p>
        )}
        {confirmDeactivate && (
          <div className="banner banner--warning" role="alert">
            Deactivating marks the account inactive. Sessions end and memberships are not deleted. Confirm to continue.
            <div className="modal__actions">
              <button type="button" className="btn btn--ghost" onClick={() => setConfirmDeactivate(false)}>Cancel</button>
              <button type="button" className="btn btn--primary" disabled={busy} onClick={() => void toggleStatus()}>
                Deactivate account
              </button>
            </div>
          </div>
        )}
        {!confirmDeactivate && (
          <button type="button" className="btn btn--secondary" disabled={busy} onClick={() => void toggleStatus()}>
            {user.status === 'active' ? 'Deactivate account' : 'Activate account'}
          </button>
        )}
        {statusError && <div className="banner banner--error" role="alert">{statusError}</div>}
      </section>

      <section className="card">
        <h2 className="card__title">Memberships ({user.membershipsTotal})</h2>
        {user.memberships.length === 0 ? (
          <p className="empty-state">No memberships.</p>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Organization</th>
                  <th>Program</th>
                  <th>Role</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {user.memberships.map((m) => (
                  <tr key={m.membershipId}>
                    <td>{m.organizationName}</td>
                    <td>{m.programName ?? '—'}</td>
                    <td>{m.role.replace(/_/g, ' ')}</td>
                    <td>{m.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
